import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  Chip,
  IconButton,
  Menu,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText as MUIListItemText,
  ListItemAvatar,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  GetApp as DownloadIcon,
  Print as PrintIcon,
  Flight as TravelIcon,
  Business as BusinessIcon,
  Hotel as AccommodationIcon,
  DirectionsCar as TransportIcon,
  Restaurant as MealsIcon,
  AttachMoney as AdvanceIcon,
  CheckCircle as ApprovedIcon,
  Schedule as PendingIcon,
  Cancel as RejectedIcon,
  Person as PersonIcon,
  DateRange as DateIcon,
  LocationOn as LocationIcon,
  Assignment as ReportIcon,
  Send as SubmitIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  AttachFile as AttachIcon,
  MonetizationOn as CostIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, differenceInDays, addDays } from "date-fns";
import { useSelector, useDispatch } from "react-redux";
import { selectUser, getUserProfile } from "../../store/slices/authSlice";
import { useAuth } from "../../components/features/auth/AuthGuard";
import { ROUTES, ROLES, PERMISSIONS } from "../../constants";
import useNotification from "../../hooks/common/useNotification";
import useConfirmDialog from "../../hooks/common/useConfirmDialog";
import { LoadingSpinner } from "../../components/common/Loading";
import { travelAPI } from "../../services/api/travel.api";
import { travelRequestTypesAPI } from "../../services/api/travelRequestTypes.api";

const TravelRequestsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { hasPermission, hasAnyPermission, hasAnyRole } = useAuth();
  const { showSuccess, showError } = useNotification();
  const { isOpen, config, openDialog, closeDialog, handleConfirm } =
    useConfirmDialog();


  // State management
  const [travelRequests, setTravelRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailRequest, setDetailRequest] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState(null);
  const [activeStep, setActiveStep] = useState(0);

  // Helper to get department name from user
  const getDepartmentName = () => {
    return (
      user?.basicEmployeeData?.department?.department_name ||
      user?.basicEmployeeData?.department?.name ||
      user?.department?.department_name ||
      user?.department?.name ||
      user?.basicEmployeeData?.department_id ||
      ""
    );
  };

  // Form state
  const [formData, setFormData] = useState({
    request_type: "", // Will be set when requestTypes load
    purpose: "",
    destination: "",
    start_date: null, // API field name
    end_date: null, // API field name
    estimated_cost: "0",
    transport_mode: "", // API field: 'flight', 'train', 'bus', 'car', 'other'
    accommodation_required: true,
    advance_required: false,
    advance_amount: "0",
    justification: "",
    urgency: "medium", // UI only, not sent to API
    notes: "",
  });


  // Mock data for development
  const mockTravelRequests = [
    {
      id: 1,
      request_number: "TR-2024-001",
      title: "Client Meeting in Nairobi",
      purpose: "Business development meeting with key client",
      request_type: "business",
      traveler_name: "John Doe",
      employee_id: "EMP001",
      department: "Sales",
      destination: "Nairobi, Kenya",
      departure_date: "2024-07-15",
      return_date: "2024-07-17",
      duration_days: 3,
      estimated_cost: 450000,
      actual_cost: null,
      status: "approved",
      urgency: "high",
      advance_required: true,
      advance_amount: 300000,
      advance_status: "approved",
      accommodation_required: true,
      transport_required: true,
      meals_required: true,
      created_date: "2024-06-20",
      approved_date: "2024-06-22",
      approved_by: "Jane Smith",
    },
    {
      id: 2,
      request_number: "TR-2024-002",
      title: "Training Workshop in Mwanza",
      purpose: "Attend project management certification workshop",
      request_type: "training",
      traveler_name: "Sarah Wilson",
      employee_id: "EMP002",
      department: "IT Department",
      destination: "Mwanza, Tanzania",
      departure_date: "2024-07-20",
      return_date: "2024-07-22",
      duration_days: 3,
      estimated_cost: 320000,
      actual_cost: null,
      status: "pending",
      urgency: "normal",
      advance_required: true,
      advance_amount: 200000,
      advance_status: "pending",
      accommodation_required: true,
      transport_required: true,
      meals_required: true,
      created_date: "2024-06-25",
      approved_date: null,
      approved_by: null,
    },
    {
      id: 3,
      request_number: "TR-2024-003",
      title: "Field Assessment in Dodoma",
      purpose: "Project site assessment and community engagement",
      request_type: "field_work",
      traveler_name: "Mike Johnson",
      employee_id: "EMP003",
      department: "Field Operations",
      destination: "Dodoma, Tanzania",
      departure_date: "2024-07-25",
      return_date: "2024-07-28",
      duration_days: 4,
      estimated_cost: 280000,
      actual_cost: null,
      status: "draft",
      urgency: "normal",
      advance_required: false,
      advance_amount: 0,
      advance_status: null,
      accommodation_required: true,
      transport_required: true,
      meals_required: true,
      created_date: "2024-06-28",
      approved_date: null,
      approved_by: null,
    },
  ];

  // Travel request types - Fetched from API
  const [requestTypes, setRequestTypes] = useState([]);

  // Request statuses - matching backend approval_status enum
  const requestStatuses = [
    { value: "draft", label: "Draft", color: "default" },
    { value: "submitted", label: "Submitted", color: "info" },
    { value: "pending", label: "Pending Approval", color: "warning" },
    { value: "approved_by_supervisor", label: "Approved by Supervisor", color: "info" },
    { value: "approved", label: "Approved", color: "success" },
    { value: "rejected", label: "Rejected", color: "error" },
    { value: "completed", label: "Completed", color: "success" },
    { value: "cancelled", label: "Cancelled", color: "default" },
  ];

  // Urgency levels
  const urgencyLevels = [
    { value: "low", label: "Low", color: "default" },
    { value: "medium", label: "Medium", color: "primary" },
    { value: "high", label: "High", color: "warning" },
    { value: "urgent", label: "Urgent", color: "error" },
  ];

  // Travel form steps
  const formSteps = [
    "Basic Information",
    "Travel Details",
    "Advance Request",
    "Review & Submit",
  ];

  // Summary cards data
  const summaryCards = [
    {
      title: "Total Requests",
      value: travelRequests.length.toString(),
      icon: <TravelIcon />,
      color: "primary",
    },
    {
      title: "Pending Approval",
      value: travelRequests
        .filter((req) => {
          const s = req.approval_status ?? req.status;
          return s === "pending" || s === "submitted";
        })
        .length.toString(),
      icon: <PendingIcon />,
      color: "warning",
    },
    {
      title: "This Month Cost",
      value: `TZS ${travelRequests.reduce((sum, req) => sum + (parseFloat(req.actual_cost) || parseFloat(req.estimated_cost) || 0), 0).toLocaleString()}`,
      icon: <CostIcon />,
      color: "success",
    },
    {
      title: "Advance Requests",
      value: travelRequests
        .filter((req) => req.advance_required)
        .length.toString(),
      icon: <AdvanceIcon />,
      color: "info",
    },
  ];

  // DataGrid columns
  const columns = [
    {
      field: "request_number",
      headerName: "Request #",
      width: 130,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TravelIcon fontSize="small" color="primary" />
          <Typography variant="body2" fontWeight="medium">
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "traveler_info",
      headerName: "Traveler",
      width: 200,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.row.traveler_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {params.row.department} • {params.row.employee_id}
          </Typography>
        </Box>
      ),
    },
    {
      field: "title",
      headerName: "Title / Type",
      width: 250,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2" fontWeight="medium">
            {params.value || params.row.purpose || "—"}
          </Typography>
          <Chip
            label={
              requestTypes.find(
                (type) => type.value === params.row.request_type
              )?.label || params.row.request_type
            }
            size="small"
            variant="outlined"
            sx={{ mt: 0.5 }}
          />
        </Box>
      ),
    },
    {
      field: "destination",
      headerName: "Destination",
      width: 150,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationIcon fontSize="small" color="action" />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "travel_dates",
      headerName: "Travel Dates",
      width: 150,
      renderCell: (params) => {
        const startDate = params.row.start_date || params.row.departure_date;
        const endDate = params.row.end_date || params.row.return_date;
        if (!startDate || !endDate) return <Typography variant="body2">-</Typography>;
        return (
          <Box>
            <Typography variant="body2">
              {format(new Date(startDate), "dd/MM/yyyy")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              to {format(new Date(endDate), "dd/MM/yyyy")}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "duration_days",
      headerName: "Duration",
      width: 100,
      renderCell: (params) => {
        const days = params.value ?? params.row.duration_days;
        if (days == null) return <Typography variant="body2">—</Typography>;
        return (
          <Typography variant="body2">
            {days} day{days !== 1 ? "s" : ""}
          </Typography>
        );
      },
    },
    {
      field: "estimated_cost",
      headerName: "Est. Cost",
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight="medium">
          TZS {(params.value ?? params.row.estimated_cost)?.toLocaleString() ?? "—"}
        </Typography>
      ),
    },
    {
      field: "urgency",
      headerName: "Urgency",
      width: 100,
      renderCell: (params) => {
        const urgency = urgencyLevels.find((u) => u.value === params.value);
        return (
          <Chip
            label={urgency?.label}
            size="small"
            color={urgency?.color}
            variant="filled"
          />
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      renderCell: (params) => {
        const approvalStatus = params.row.approval_status || params.row.status;
        const status = requestStatuses.find((s) => s.value === approvalStatus) || 
                      requestStatuses.find((s) => s.value === params.value);
        const isRejected = approvalStatus === "rejected" || params.value === "rejected";
        const statusLabel = status?.label || approvalStatus || params.value;

        return (
          <Box>
            <Chip
              label={statusLabel}
              size="small"
              color={status?.color || "default"}
              variant="filled"
              title={
                isRejected && params.row.rejection_reason
                  ? `Rejected: ${params.row.rejection_reason}`
                  : statusLabel
              }
            />
            {params.row.approver && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                Approver: {params.row.approver.first_name} {params.row.approver.sur_name}
              </Typography>
            )}
            {isRejected && params.row.rejection_reason && (
              <Typography
                variant="caption"
                color="error"
                display="block"
                sx={{ mt: 0.5 }}
              >
                {params.row.rejection_reason.length > 30
                  ? `${params.row.rejection_reason.substring(0, 30)}...`
                  : params.row.rejection_reason}
              </Typography>
            )}
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => {
            setAnchorEl(e.currentTarget);
            setSelectedRequest(params.row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ];

  // Load travel requests data
  useEffect(() => {
    fetchTravelRequests();
    fetchTravelRequestTypes();
  }, []);

  // Update form when request types are loaded or user data changes
  useEffect(() => {
    if (requestTypes.length > 0 && !editingRequest) {
      // Set first available request type if form is empty or has default "personal"
      if (!formData.request_type || formData.request_type === "personal") {
        const firstType = requestTypes[0]?.value;
        if (firstType) {
          setFormData((prev) => ({
            ...prev,
            request_type: firstType,
          }));
        }
      }
    }
  }, [requestTypes]);

  // Update department when user data is available
  useEffect(() => {
    if (user && !editingRequest) {
      const departmentName =
        user?.basicEmployeeData?.department?.department_name ||
        user?.basicEmployeeData?.department?.name ||
        user?.department?.department_name ||
        user?.department?.name ||
        user?.basicEmployeeData?.department_id ||
        "";
      
      if (departmentName && departmentName !== formData.department) {
        setFormData((prev) => ({
          ...prev,
          department: departmentName,
          traveler_name: `${user?.first_name || ""} ${user?.middle_name || ""} ${user?.sur_name || ""}`.trim(),
          employee_id: user?.basicEmployeeData?.registration_number || user?.employee_id || "",
        }));
      }
    }
  }, [user]);

  // Reset form when dialog opens for new request (not editing)
  useEffect(() => {
    if (dialogOpen && !editingRequest) {
      const departmentName = getDepartmentName();
      const firstRequestType = requestTypes.length > 0 ? requestTypes[0]?.value : "";
      
      setFormData({
        request_type: firstRequestType,
        purpose: "",
        destination: "",
        start_date: null,
        end_date: null,
        estimated_cost: "0",
        transport_mode: "",
        accommodation_required: true,
        advance_required: false,
        advance_amount: "0",
        justification: "",
        urgency: "medium",
        notes: "",
      });
      setActiveStep(0);
    }
  }, [dialogOpen, editingRequest, requestTypes, user]);

  const fetchTravelRequestTypes = async () => {
    try {
      const response = await travelRequestTypesAPI.getAll({
        active_only: true,
      });
      const types = response.data.data.travelRequestTypes || [];

      // Map the types to the format expected by the component
      const mappedTypes = types.map((type) => ({
        value: type.code,
        label: type.name,
        icon: <BusinessIcon />, // Default icon, can be improved based on type.icon
        requiresAdvance: type.requires_advance,
      }));

      setRequestTypes(mappedTypes);
    } catch (error) {
      console.error("Error fetching travel request types:", error);
      // Fallback to empty array if fetch fails
      setRequestTypes([]);
    }
  };

  // Format numeric input with thousand separators (commas) for display
  const formatNumberWithCommas = (value) => {
    if (value == null || value === "") return "";
    const s = String(value).replace(/,/g, "");
    if (s === "" || s === ".") return s;
    const parts = s.split(".");
    const intPart = (parts[0] || "").replace(/\D/g, "") || "0";
    const decPart = parts[1] != null ? parts[1].replace(/\D/g, "").slice(0, 2) : null;
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return decPart != null && decPart !== "" ? `${formattedInt}.${decPart}` : formattedInt;
  };

  const fetchTravelRequests = async () => {
    try {
      setLoading(true);
      const response = await travelAPI.getAllRequests();
      const raw = response.data.data?.requests || response.data?.requests || [];
      // Normalize API response so table columns have data (user, requestType are nested)
      const normalized = raw.map((req) => {
        const user = req.user || {};
        const names = [user.first_name, user.middle_name, user.sur_name].filter(Boolean);
        const travelerName = names.join(" ").trim() || "—";
        const dept = user.basicEmployeeData?.department;
        const department = dept?.department_name || dept?.name || "—";
        const employeeId = user.basicEmployeeData?.registration_number || user.employee_id || "—";
        const requestType = req.requestType || {};
        const typeLabel = requestType.name || requestType.code || req.request_type || "—";
        const typeCode = requestType.code || req.request_type || "";
        const start = req.start_date || req.departure_date;
        const end = req.end_date || req.return_date;
        const durationDays = start && end
          ? differenceInDays(new Date(end), new Date(start)) + 1
          : null;
        return {
          ...req,
          id: req.id,
          request_number: req.request_number || `TR-${req.id}`,
          traveler_name: travelerName,
          department,
          employee_id: employeeId,
          title: req.title || req.purpose || req.destination || "—",
          request_type: typeCode || typeLabel,
          destination: req.destination || "—",
          start_date: start,
          end_date: end,
          duration_days: durationDays,
          estimated_cost: req.estimated_cost,
          actual_cost: req.actual_cost,
          status: req.approval_status ?? req.status,
          approval_status: req.approval_status ?? req.status,
          urgency: req.urgency || "medium",
          advance_required: req.advance_required ?? false,
          advance_amount: req.advance_amount ?? 0,
        };
      });
      setTravelRequests(normalized);
    } catch (error) {
      console.error("Error fetching travel requests:", error);
      showError("Failed to fetch travel requests");
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission. submitForApproval: true = full submit, false = save as draft
  const handleSubmit = async (submitForApproval = false) => {
    try {
      // Validate required fields
      if (!formData.destination || !formData.purpose) {
        showError(
          "Please fill in all required fields (Destination, Purpose)"
        );
        return;
      }

      if (!formData.request_type) {
        showError("Please select a request type");
        return;
      }

      if (!formData.start_date || !formData.end_date) {
        showError("Please select both start and end dates");
        return;
      }

      if (!formData.transport_mode) {
        showError("Please select transport mode");
        return;
      }

      // Format the data for backend - only send API-expected fields
      const requestData = {
        destination: formData.destination,
        purpose: formData.purpose,
        start_date: formData.start_date
          ? formData.start_date.toISOString().split("T")[0]
          : null,
        end_date: formData.end_date
          ? formData.end_date.toISOString().split("T")[0]
          : null,
        estimated_cost: parseFloat(String(formData.estimated_cost || 0).replace(/,/g, "")) || 0,
        transport_mode: formData.transport_mode,
        accommodation_required: formData.accommodation_required,
        request_type: formData.request_type,
        submit: submitForApproval,
        advance_required: formData.advance_required === true || formData.advance_required === "true",
        advance_amount: formData.advance_required ? (parseFloat(String(formData.advance_amount || 0).replace(/,/g, "")) || 0) : 0,
        // Optional notes
        ...(formData.notes && { notes: formData.notes }),
      };

      console.log(
        "Submitting travel request data:",
        JSON.stringify(requestData, null, 2)
      );

      if (editingRequest) {
        await travelAPI.updateRequest(editingRequest.id, requestData);
        showSuccess("Travel request updated successfully");
      } else {
        await travelAPI.createRequest(requestData);
        showSuccess(
          submitForApproval
            ? "Travel request submitted for approval successfully"
            : "Travel request saved as draft"
        );
      }
      setDialogOpen(false);
      resetForm();
      fetchTravelRequests();
    } catch (error) {
      console.error("Travel request error details:", error.response?.data);
      showError("Failed to save travel request");
    }
  };

  const resetForm = () => {
    const firstRequestType = requestTypes.length > 0 ? requestTypes[0]?.value : "";
    
    setFormData({
      request_type: firstRequestType,
      purpose: "",
      destination: "",
      start_date: null,
      end_date: null,
      estimated_cost: "0",
      transport_mode: "",
      accommodation_required: true,
      advance_required: false,
      advance_amount: "0",
      justification: "",
      urgency: "medium",
      notes: "",
    });
    // Removed setTravelExpenses - expense items not in API
    setActiveStep(0);
    setEditingRequest(null);
  };

  // Handle edit
  const handleEdit = (request) => {
    setFormData({ ...request });
    setEditingRequest(request);
    setDialogOpen(true);
    setAnchorEl(null);
  };

  // Handle delete
  const handleDelete = (request) => {
    openDialog({
      title: "Delete Travel Request",
      message: `Are you sure you want to delete this travel request${request.purpose ? `: ${request.purpose.substring(0, 50)}${request.purpose.length > 50 ? "…" : ""}` : ""}?`,
      onConfirm: async () => {
        try {
          await travelAPI.deleteRequest(request.id);
          showSuccess("Travel request deleted successfully");
          fetchTravelRequests();
        } catch (error) {
          showError("Failed to delete travel request");
        }
      },
    });
    setAnchorEl(null);
  };

  // Handle approval actions
  const handleApprove = async (request) => {
    try {
      await travelAPI.approveRequest(request.id, { comments: "Approved" });
      showSuccess("Travel request approved successfully");
      fetchTravelRequests();
    } catch (error) {
      console.error("Error approving travel request:", error);
      showError("Failed to approve travel request");
    }
    setAnchorEl(null);
  };

  const handleReject = async (request) => {
    try {
      await travelAPI.rejectRequest(request.id, { rejection_reason: "Rejected" });
      showSuccess("Travel request rejected");
      fetchTravelRequests();
    } catch (error) {
      console.error("Error rejecting travel request:", error);
      showError("Failed to reject travel request");
    }
    setAnchorEl(null);
  };

  // Handle resubmit
  const handleResubmit = async (request) => {
    try {
      await travelAPI.resubmitRequest(request.id);
      showSuccess("Travel request resubmitted successfully");
      fetchTravelRequests();
    } catch (error) {
      console.error("Error resubmitting travel request:", error);
      showError("Failed to resubmit travel request");
    }
    setAnchorEl(null);
  };

  // Add/Remove expense items
  // Expense items removed - not part of API

  // Submit draft for approval (draft → full submission)
  const handleSubmitForApproval = async (request) => {
    try {
      await travelAPI.submitRequest(request.id);
      showSuccess("Travel request submitted for approval successfully");
      fetchTravelRequests();
      setViewDialogOpen(false);
    } catch (error) {
      console.error("Error submitting travel request:", error);
      showError(error.response?.data?.message || "Failed to submit travel request for approval");
    }
    setAnchorEl(null);
  };

  // Filter travel requests
  const filteredTravelRequests = travelRequests.filter((request) => {
    const status = request.approval_status ?? request.status;
    const matchesSearch =
      (request.destination || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (request.purpose || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (request.request_number || "").toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || status === statusFilter;
    const matchesType = !typeFilter || request.request_type === typeFilter;

    return matchesSearch && matchesStatus && matchesType;
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Personal Travel Requests
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Submit and manage your personal travel requests for approval
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {summaryCards.map((card, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="h6" component="div">
                        {card.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {card.title}
                      </Typography>
                    </Box>
                    <Box sx={{ color: `${card.color}.main` }}>{card.icon}</Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Main Content */}
        <Card>
          <CardContent>
            {/* Filters and Actions */}
            <Box sx={{ mb: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search travel requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      {requestStatuses.map((status) => (
                        <MenuItem key={status.value} value={status.value}>
                          {status.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* <Grid item xs={12} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      label="Type"
                    >
                      <MenuItem value="">All Types</MenuItem>
                      {requestTypes.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid> */}
                {/* <Grid item xs={12} md={2}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Department"
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                  />
                </Grid> */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {hasAnyPermission([
                      PERMISSIONS.MANAGE_TRAVEL_REQUESTS,
                      PERMISSIONS.VIEW_TRAVEL_REQUESTS,
                      "travel:view",
                      "travel:create",
                      "travel:update",
                      "travel_requests:view",
                      "travel_requests:create",
                      "travel_requests:update",
                    ]) && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setDialogOpen(true)}
                      >
                        Add Personal Travel Request
                      </Button>
                    )}
                    <Button
                      variant="outlined"
                      startIcon={<AdvanceIcon />}
                      onClick={() => navigate("/travel/advances")}
                    >
                      Advances
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={() => {
                        /* Export function */
                      }}
                    >
                      Export
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Content Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
            >
              <Tab label="Travel Requests" />
              <Tab label="Approval Workflow" />
              <Tab label="Travel Reports" />
            </Tabs>

            {/* Travel Requests Tab */}
            {activeTab === 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ height: 600, width: "100%" }}>
                  <DataGrid
                    rows={filteredTravelRequests}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    disableSelectionOnClick
                    getRowHeight={() => "auto"}
                    sx={{
                      "& .MuiDataGrid-cell": {
                        py: 1,
                      },
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Approval Workflow Tab */}
            {activeTab === 1 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Travel request approval workflow and status tracking will be
                    displayed here.
                  </Typography>
                </Alert>
              </Box>
            )}

            {/* Travel Reports Tab */}
            {activeTab === 2 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    Travel analytics, expense reports, and compliance tracking
                    will be displayed here.
                  </Typography>
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Actions Menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem
            onClick={async () => {
              setAnchorEl(null);
              if (selectedRequest?.id) {
                setDetailLoading(true);
                setViewDialogOpen(true);
                try {
                  const res = await travelAPI.getRequestById(selectedRequest.id);
                  setDetailRequest(res?.data?.data ?? res?.data ?? selectedRequest);
                } catch {
                  setDetailRequest(selectedRequest);
                } finally {
                  setDetailLoading(false);
                }
              } else {
                setDetailRequest(selectedRequest);
                setViewDialogOpen(true);
              }
            }}
          >
            <ListItemIcon>
              <ViewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View Details</ListItemText>
          </MenuItem>
          {(selectedRequest?.approval_status === "draft" || selectedRequest?.status === "draft") && (
            <MenuItem onClick={() => handleSubmitForApproval(selectedRequest)}>
              <ListItemIcon>
                <SubmitIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Submit for Approval</ListItemText>
            </MenuItem>
          )}
          {((selectedRequest?.approval_status === "draft" || selectedRequest?.status === "draft" ||
              selectedRequest?.approval_status === "rejected" || selectedRequest?.status === "rejected") &&
            (selectedRequest?.user_id === user?.id || hasPermission(PERMISSIONS.MANAGE_TRAVEL_REQUESTS) ||
              hasAnyPermission?.(["travel_requests:update", "travel:update"]))) && (
              <MenuItem onClick={() => handleEdit(selectedRequest)}>
                <ListItemIcon>
                  <EditIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit Request</ListItemText>
              </MenuItem>
            )}
          {(selectedRequest?.approval_status === "rejected" || selectedRequest?.status === "rejected") && (
            <MenuItem onClick={() => handleResubmit(selectedRequest)}>
              <ListItemIcon>
                <SubmitIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Resubmit Request</ListItemText>
            </MenuItem>
          )}
          {(selectedRequest?.approval_status === "pending" || selectedRequest?.status === "pending") &&
            hasPermission(PERMISSIONS.APPROVE_TRAVEL_REQUESTS) && (
              <>
                <Divider />
                <MenuItem onClick={() => handleApprove(selectedRequest)}>
                  <ListItemIcon>
                    <ApprovedIcon fontSize="small" color="success" />
                  </ListItemIcon>
                  <ListItemText>Approve</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => handleReject(selectedRequest)}>
                  <ListItemIcon>
                    <RejectedIcon fontSize="small" color="error" />
                  </ListItemIcon>
                  <ListItemText>Reject</ListItemText>
                </MenuItem>
              </>
            )}
          {selectedRequest?.advance_required && (
            <MenuItem
              onClick={() =>
                navigate(`/travel/advances?requestId=${selectedRequest?.id}`)
              }
            >
              <ListItemIcon>
                <AdvanceIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Manage Advance</ListItemText>
            </MenuItem>
          )}
          <Divider />
          <MenuItem
            onClick={() => {
              /* Print travel request */
            }}
          >
            <ListItemIcon>
              <PrintIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Print Request</ListItemText>
          </MenuItem>
          {hasPermission(PERMISSIONS.MANAGE_TRAVEL_REQUESTS) &&
            (selectedRequest?.approval_status === "draft" || selectedRequest?.status === "draft") && (
              <MenuItem
                onClick={() => handleDelete(selectedRequest)}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Delete Request</ListItemText>
              </MenuItem>
            )}
        </Menu>

        {/* Add/Edit Travel Request Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            {editingRequest
              ? "Edit Personal Travel Request"
              : "Create Personal Travel Request"}
          </DialogTitle>
          <DialogContent>
            <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
              {formSteps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {/* Step 1: Basic Information */}
            {activeStep === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Request Type</InputLabel>
                    <Select
                      value={formData.request_type || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          request_type: e.target.value,
                        })
                      }
                      label="Request Type"
                      displayEmpty
                      disabled={requestTypes.length === 0}
                    >
                      {requestTypes.length === 0 ? (
                        <MenuItem disabled value="">
                          <em>Loading request types...</em>
                        </MenuItem>
                      ) : (
                        requestTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {type.icon}
                              {type.label}
                            </Box>
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Urgency</InputLabel>
                    <Select
                      value={formData.urgency}
                      onChange={(e) =>
                        setFormData({ ...formData, urgency: e.target.value })
                      }
                      label="Urgency"
                    >
                      {urgencyLevels.map((urgency) => (
                        <MenuItem key={urgency.value} value={urgency.value}>
                          {urgency.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Purpose of Travel"
                    value={formData.purpose}
                    onChange={(e) =>
                      setFormData({ ...formData, purpose: e.target.value })
                    }
                    multiline
                    rows={3}
                    required
                    placeholder="Describe the purpose of your travel (e.g., Client meeting, Training, Field work...)"
                  />
                </Grid>
              </Grid>
            )}

            {/* Step 2: Travel Details */}
            {activeStep === 1 && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Destination"
                    value={formData.destination}
                    onChange={(e) =>
                      setFormData({ ...formData, destination: e.target.value })
                    }
                    required
                    placeholder="e.g., Nairobi, Kenya"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="Start Date"
                    value={formData.start_date}
                    onChange={(date) =>
                      setFormData({ ...formData, start_date: date })
                    }
                    minDate={new Date()}
                    slotProps={{
                      textField: { fullWidth: true, required: true },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <DatePicker
                    label="End Date"
                    value={formData.end_date}
                    onChange={(date) =>
                      setFormData({ ...formData, end_date: date })
                    }
                    minDate={formData.start_date || new Date()}
                    slotProps={{
                      textField: { fullWidth: true, required: true },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Transport Mode</InputLabel>
                    <Select
                      value={formData.transport_mode}
                      onChange={(e) =>
                        setFormData({ ...formData, transport_mode: e.target.value })
                      }
                      label="Transport Mode"
                    >
                      <MenuItem value="flight">Flight</MenuItem>
                      <MenuItem value="train">Train</MenuItem>
                      <MenuItem value="bus">Bus</MenuItem>
                      <MenuItem value="car">Car</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Estimated Total Cost (TZS)"
                    type="text"
                    inputMode="decimal"
                    value={formatNumberWithCommas(formData.estimated_cost)}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimated_cost: e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"),
                      })
                    }
                    required
                    placeholder="e.g. 700,000"
                    helperText="Total estimated cost for this travel"
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControl component="fieldset">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AccommodationIcon />
                      <Typography variant="body2">Accommodation Required</Typography>
                      <input
                        type="checkbox"
                        checked={formData.accommodation_required}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            accommodation_required: e.target.checked,
                          })
                        }
                      />
                    </Box>
                  </FormControl>
                </Grid>
              </Grid>
            )}

            {/* Step 3: Advance Request (Optional) */}
            {activeStep === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Travel Advance Request (Optional)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <FormControl component="fieldset">
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <input
                          type="checkbox"
                          checked={formData.advance_required}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              advance_required: e.target.checked,
                            })
                          }
                        />
                        <Typography variant="body2">
                          I request a travel advance for this trip
                        </Typography>
                      </Box>
                    </FormControl>
                  </Grid>
                  {formData.advance_required && (
                    <>
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Advance Amount (TZS)"
                          type="text"
                          inputMode="decimal"
                          value={formatNumberWithCommas(formData.advance_amount)}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              advance_amount: e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1"),
                            })
                          }
                          required
                          placeholder="e.g. 700,000"
                          helperText={`Recommended: 70% of estimated cost (${Math.round((parseFloat(String(formData.estimated_cost || 0).replace(/,/g, "")) || 0) * 0.7).toLocaleString()} TZS)`}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mt: 2 }}
                        >
                          Estimated Total Cost: TZS{" "}
                          {(parseFloat(formData.estimated_cost) || 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Advance Justification"
                          value={formData.justification}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              justification: e.target.value,
                            })
                          }
                          multiline
                          rows={3}
                          required
                          placeholder="Explain why you need this advance amount..."
                        />
                      </Grid>
                    </>
                  )}
                  {!formData.advance_required && (
                    <Grid item xs={12}>
                      <Alert severity="info">
                        You can request a travel advance later if needed. This can be done after your travel request is approved.
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </Box>
            )}

            {/* Step 4: Review & Submit */}
            {activeStep === 3 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Review Travel Request
                </Typography>

                <Accordion defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Travel Summary
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Request Type
                        </Typography>
                        <Typography variant="body1">
                          {requestTypes.find(t => t.value === formData.request_type)?.label || formData.request_type || "Not set"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Destination
                        </Typography>
                        <Typography variant="body1">
                          {formData.destination || "Not set"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Purpose
                        </Typography>
                        <Typography variant="body1">
                          {formData.purpose || "Not set"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Start Date
                        </Typography>
                        <Typography variant="body1">
                          {formData.start_date
                            ? format(
                                new Date(formData.start_date),
                                "dd/MM/yyyy"
                              )
                            : "Not set"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          End Date
                        </Typography>
                        <Typography variant="body1">
                          {formData.end_date
                            ? format(
                                new Date(formData.end_date),
                                "dd/MM/yyyy"
                              )
                            : "Not set"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Transport Mode
                        </Typography>
                        <Typography variant="body1">
                          {formData.transport_mode ? formData.transport_mode.charAt(0).toUpperCase() + formData.transport_mode.slice(1) : "Not set"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary">
                          Accommodation Required
                        </Typography>
                        <Typography variant="body1">
                          {formData.accommodation_required ? "Yes" : "No"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" fontWeight="medium">
                      Financial Summary
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Estimated Cost
                        </Typography>
                        <Typography variant="h6" color="primary">
                          TZS {(formData.estimated_cost || 0).toLocaleString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Advance Requested
                        </Typography>
                        <Typography
                          variant="h6"
                          color={
                            formData.advance_required
                              ? "warning.main"
                              : "text.secondary"
                          }
                        >
                          {formData.advance_required
                            ? `TZS ${(formData.advance_amount || 0).toLocaleString()}`
                            : "No advance requested"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>

                <Box sx={{ mt: 3 }}>
                  <TextField
                    fullWidth
                    label="Additional Notes"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    multiline
                    rows={3}
                    placeholder="Any additional information or special requirements..."
                  />
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            {activeStep > 0 && (
              <Button onClick={() => setActiveStep(activeStep - 1)}>
                Back
              </Button>
            )}
            {activeStep < formSteps.length - 1 ? (
              <Button
                variant="contained"
                onClick={() => setActiveStep(activeStep + 1)}
              >
                Next
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => handleSubmit(false)}
                  variant="outlined"
                  startIcon={<SaveIcon />}
                >
                  Save Draft
                </Button>
                <Button
                  onClick={() => handleSubmit(true)}
                  variant="contained"
                  startIcon={<SubmitIcon />}
                >
                  Submit for Approval
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* View Travel Request Dialog */}
        <Dialog
          open={viewDialogOpen}
          onClose={() => {
            setViewDialogOpen(false);
            setDetailRequest(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Travel Request Details - {(detailRequest ?? selectedRequest)?.request_number ?? (detailRequest ?? selectedRequest)?.id}
          </DialogTitle>
          <DialogContent>
            {detailLoading && (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography color="text.secondary">Loading details…</Typography>
              </Box>
            )}
            {(detailRequest ?? selectedRequest) && !detailLoading && (() => {
              const req = detailRequest ?? selectedRequest;
              const advances = req.travelAdvances || [];
              return (
              <Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Travel Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Title / Purpose
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {req.title || req.purpose || "—"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Type
                            </Typography>
                            <Chip
                              label={
                                req.requestType?.name ||
                                req.requestType?.code ||
                                requestTypes.find((t) => t.value === req.request_type)?.label ||
                                req.request_type ||
                                "—"
                              }
                              size="small"
                              variant="outlined"
                              sx={{ mb: 2 }}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                              Purpose
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {req.purpose}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Destination
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {req.destination}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Duration
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {(() => {
                                if (req.duration_days != null) return `${req.duration_days} days`;
                                const s = req.start_date || req.departure_date;
                                const e = req.end_date || req.return_date;
                                if (s && e) {
                                  const days = differenceInDays(new Date(e), new Date(s)) + 1;
                                  return `${days} days`;
                                }
                                return "—";
                              })()}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Departure Date
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {format(
                                new Date(req.start_date || req.departure_date),
                                "dd/MM/yyyy"
                              )}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Return Date
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                              {format(
                                new Date(req.end_date || req.return_date),
                                "dd/MM/yyyy"
                              )}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Traveler Details
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body1">
                            {req.traveler_name || req.user ? [req.user.first_name, req.user.sur_name].filter(Boolean).join(" ") : ""}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Employee ID
                          </Typography>
                          <Typography variant="body1">
                            {req.employee_id || req.user_id}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Department
                          </Typography>
                          <Typography variant="body1">
                            {req.department || req.user?.basicEmployeeData?.department?.department_name || "—"}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Status
                          </Typography>
                          <Chip
                            label={
                              requestStatuses.find(
                                (s) => s.value === (req.approval_status ?? req.status))
                              ?.label || (req.approval_status ?? req.status)
                            }
                            size="small"
                            color={
                              requestStatuses.find(
                                (s) => s.value === (req.approval_status ?? req.status))
                              ?.color || "default"
                            }
                            sx={{ mt: 0.5 }}
                          />
                          {(req.approval_status === "rejected" || req.status === "rejected") &&
                            req.rejection_reason && (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  variant="body2"
                                  color="error"
                                  fontWeight="medium"
                                >
                                  Rejection Reason:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {req.rejection_reason}
                                </Typography>
                                {req.rejected_at && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 0.5 }}
                                  >
                                    Rejected on:{" "}
                                    {format(
                                      new Date(req.rejected_at),
                                      "dd/MM/yyyy HH:mm"
                                    )}
                                  </Typography>
                                )}
                              </Box>
                            )}
                        </Box>
                      </CardContent>
                    </Card>

                    <Card variant="outlined" sx={{ mt: 2 }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                          Financial Details
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Estimated Cost
                          </Typography>
                          <Typography variant="h6" color="primary">
                            TZS{" "}
                            {req.estimated_cost != null && Number(req.estimated_cost).toLocaleString()}
                          </Typography>
                        </Box>
                        {(req.advance_required || advances.length > 0) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                              Advance Amount
                            </Typography>
                            <Typography variant="h6" color="warning.main">
                              TZS{" "}
                              {(req.advance_amount != null ? Number(req.advance_amount) : advances.reduce((s, a) => s + Number(a.total_cost || 0), 0)).toLocaleString()}
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  {advances.length > 0 && (
                    <Grid item xs={12}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="h6" sx={{ mb: 2 }}>
                            Travel Advance(s)
                          </Typography>
                          <List dense>
                            {advances.map((adv) => (
                              <ListItem key={adv.id} sx={{ borderBottom: "1px solid", borderColor: "divider" }}>
                                <ListItemText
                                  primary={`TA-${adv.id} • TZS ${Number(adv.total_cost || 0).toLocaleString()}`}
                                  secondary={
                                    <>
                                      {format(new Date(adv.departure_date), "dd/MM/yyyy")} – {format(new Date(adv.return_date), "dd/MM/yyyy")}
                                      {" • "}
                                      <Chip label={adv.approval_status || "draft"} size="small" sx={{ ml: 0.5 }} />
                                    </>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
              );
            })()}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => {
                /* Print travel request */
              }}
            >
              Print Request
            </Button>
            {(selectedRequest?.approval_status === "draft" || selectedRequest?.status === "draft") && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SubmitIcon />}
                onClick={() => handleSubmitForApproval(selectedRequest)}
              >
                Submit for Approval
              </Button>
            )}
            {((selectedRequest?.approval_status === "draft" || selectedRequest?.status === "draft" ||
                selectedRequest?.approval_status === "rejected" || selectedRequest?.status === "rejected") &&
              (selectedRequest?.user_id === user?.id || hasPermission(PERMISSIONS.MANAGE_TRAVEL_REQUESTS) ||
                hasAnyPermission?.(["travel_requests:update", "travel:update"]))) && (
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleEdit(selectedRequest);
                }}
              >
                Edit Request
              </Button>
            )}
            {(selectedRequest?.approval_status === "rejected" || selectedRequest?.status === "rejected") && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SubmitIcon />}
                onClick={() => {
                  setViewDialogOpen(false);
                  handleResubmit(selectedRequest);
                }}
              >
                Resubmit Request
              </Button>
            )}
          </DialogActions>
        </Dialog>

        {/* Confirm Dialog */}
        {isOpen && (
          <Dialog open={isOpen} onClose={closeDialog}>
            <DialogTitle>{config.title}</DialogTitle>
            <DialogContent>
              <Typography>{config.message}</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>Cancel</Button>
              <Button onClick={handleConfirm} color="error" variant="contained">
                Confirm
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default TravelRequestsPage;
